import { Clock, MessagesSquare, Plus } from 'lucide-react';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { useDiscussoesControllerListar } from '@/api/generated/discussões-técnicas/discussões-técnicas';
import { DiscussoesControllerListarStatus } from '@/api/generated/model';
import {
  classeDaDiscussao,
  classeUrgenciaPrazo,
  diasParaEncerramentoAutomatico,
  ROTULO_STATUS,
  rotuloDaDiscussao,
  rotuloDiasParaEncerrar,
} from '@/features/discussoes/rotulos';
import { EstadoErro, EstadoVazio } from '@/shared/components/estados';
import { rotaDaDiscussao, ROTAS } from '@/shared/lib/rotas';
import { usePermissoes } from '@/shared/permissoes/usePermissoes';
import { Badge } from '@/shared/ui/badge';
import { Button } from '@/shared/ui/button';
import { Input } from '@/shared/ui/input';
import { Paginacao } from '@/shared/ui/pagination';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/shared/ui/select';
import { Skeleton } from '@/shared/ui/skeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/shared/ui/table';

const TODOS = 'todos';

/**
 * Discussões técnicas (F5) — lado do médico.
 *
 * Compartilhado com o advisor: a mesma rota `GET /discussoes` já filtra pelo
 * participante no backend, então esta tela não precisa saber quem é quem.
 */
export function DiscussoesPage() {
  const navigate = useNavigate();
  const { pode } = usePermissoes();

  const [pagina, setPagina] = useState(1);
  const [busca, setBusca] = useState('');
  const [status, setStatus] = useState<string>(TODOS);

  const { data, isPending, isError, refetch } = useDiscussoesControllerListar({
    pagina,
    porPagina: 20,
    ...(busca.trim() ? { busca: busca.trim() } : {}),
    ...(status !== TODOS ? { status: status as DiscussoesControllerListarStatus } : {}),
  });

  const podeCriar = pode('discussoes.criar');

  function aoFiltrar(aplicar: () => void) {
    aplicar();
    setPagina(1);
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-3">
        <div className="flex flex-col sm:flex-row gap-3 flex-1">
          <Input
            value={busca}
            onChange={(e) => aoFiltrar(() => setBusca(e.target.value))}
            placeholder="Buscar por assunto ou código..."
            className="sm:max-w-xs"
          />

          <Select value={status} onValueChange={(v) => aoFiltrar(() => setStatus(v))}>
            <SelectTrigger className="sm:w-52" aria-label="Filtrar por status">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={TODOS}>Todos os status</SelectItem>
              {Object.values(DiscussoesControllerListarStatus).map((valor) => (
                <SelectItem key={valor} value={valor}>
                  {ROTULO_STATUS[valor] ?? valor}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {podeCriar && (
          <Button onClick={() => navigate(ROTAS.discussaoNova)}>
            <Plus className="w-4 h-4" />
            Nova discussão
          </Button>
        )}
      </div>

      <div className="bg-surface-raised rounded-lg border-2 border-brand/20 overflow-hidden">
        <div className="px-6 py-4 bg-gradient-to-r from-brand/5 to-transparent border-b border-brand/10">
          <h3 className="text-sm font-semibold text-brand uppercase tracking-wide">
            Discussões técnicas
          </h3>
        </div>

        {isPending ? (
          <div className="p-6 space-y-3">
            {[0, 1, 2, 3].map((i) => (
              <Skeleton key={i} className="h-10 rounded-md" />
            ))}
          </div>
        ) : isError || !data ? (
          <EstadoErro aoTentarDeNovo={() => void refetch()} />
        ) : data.itens.length === 0 ? (
          <EstadoVazio
            icone={MessagesSquare}
            titulo="Nenhuma discussão encontrada"
            descricao={
              busca || status !== TODOS
                ? 'Tente outro termo ou limpe os filtros.'
                : 'Abra uma discussão técnica com um especialista para começar.'
            }
          />
        ) : (
          <>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Código</TableHead>
                    <TableHead>Assunto</TableHead>
                    <TableHead>Advisor</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Prazo</TableHead>
                    <TableHead className="text-right">Não lidas</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.itens.map((discussao) => {
                    const diasParaEncerrar = diasParaEncerramentoAutomatico(
                      discussao.prazoAutoEncerramento,
                    );

                    return (
                      <TableRow
                        key={discussao.id}
                        className="cursor-pointer"
                        onClick={() => navigate(rotaDaDiscussao(discussao.id))}
                      >
                        <TableCell className="font-mono text-xs text-muted-foreground">
                          {discussao.codigo}
                        </TableCell>
                        <TableCell>
                          <span className="font-medium text-foreground">{discussao.assunto}</span>
                          {discussao.pacienteNome && (
                            <p className="text-xs text-muted-foreground mt-0.5">
                              {discussao.pacienteNome}
                            </p>
                          )}
                        </TableCell>
                        <TableCell className="text-foreground">{discussao.assessorNome ?? '—'}</TableCell>
                        <TableCell>
                          <Badge variant="outline" className={classeDaDiscussao(discussao)}>
                            {rotuloDaDiscussao(discussao)}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          {diasParaEncerrar !== null ? (
                            <span
                              className={`inline-flex items-center gap-1 text-xs font-medium ${classeUrgenciaPrazo(diasParaEncerrar)}`}
                            >
                              <Clock className="w-3.5 h-3.5 shrink-0" />
                              {rotuloDiasParaEncerrar(diasParaEncerrar)}
                            </span>
                          ) : (
                            <span className="text-muted-foreground">—</span>
                          )}
                        </TableCell>
                        <TableCell className="text-right">
                          {discussao.naoLidas > 0 ? (
                            <Badge className="bg-brand text-brand-foreground">
                              {discussao.naoLidas}
                            </Badge>
                          ) : (
                            <span className="text-muted-foreground">—</span>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>

            <Paginacao
              pagina={data.pagina}
              totalPaginas={data.totalPaginas}
              total={data.total}
              porPagina={data.porPagina}
              aoMudarPagina={setPagina}
            />
          </>
        )}
      </div>
    </div>
  );
}
